import type { ModuleStatus } from '../api/types';

// ── Dot ─────────────────────────────────────────────────────────────
export function Dot({ color = 'var(--green)', size = 7, pulse = false }: { color?: string; size?: number; pulse?: boolean }) {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', width: size, height: size, flexShrink: 0 }}>
      {pulse && <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color, opacity: 0.35, animation: 'ping 2s cubic-bezier(0,0,0.2,1) infinite' }} />}
      <span style={{ display: 'block', width: size, height: size, borderRadius: '50%', background: color }} />
    </span>
  );
}

// ── Mono ────────────────────────────────────────────────────────────
export function Mono({ children, color = 'var(--text-secondary)', style = {} }: { children: React.ReactNode; color?: string; style?: React.CSSProperties }) {
  return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color, ...style }}>{children}</span>;
}

// ── Spinner ─────────────────────────────────────────────────────────
export function Spinner({ size = 14 }: { size?: number }) {
  return <span style={{ display: 'inline-block', width: size, height: size, border: '2px solid var(--border-subtle)', borderTopColor: 'var(--blue)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />;
}

// ── StatusChip — now with icon prefix for non-color-only indication ─
type ChipVariant = 'success' | 'warning' | 'error' | 'info' | 'muted' | 'stale' | 'proxy' | 'unknown';

const CHIP: Record<ChipVariant, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'var(--green-bg)', border: 'var(--green-border)', text: 'var(--green)', icon: '✓' },
  warning: { bg: 'var(--yellow-bg)', border: 'var(--yellow-border)', text: 'var(--yellow)', icon: '⚠' },
  error:   { bg: 'var(--red-bg)', border: 'var(--red-border)', text: 'var(--red)', icon: '●' },
  info:    { bg: 'var(--blue-bg)', border: 'var(--blue-border)', text: 'var(--blue)', icon: 'ℹ' },
  muted:   { bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.25)', text: 'var(--text-ghost)', icon: '—' },
  stale:   { bg: 'var(--orange-bg)', border: 'var(--orange-border)', text: 'var(--orange)', icon: '◷' },
  proxy:   { bg: 'var(--purple-bg)', border: 'var(--purple-border)', text: 'var(--purple)', icon: '~' },
  unknown: { bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.25)', text: 'var(--text-ghost)', icon: '?' },
};

export function StatusChip({ label, variant = 'muted', dot = false, showIcon = false }: { label: string; variant?: ChipVariant; dot?: boolean; showIcon?: boolean }) {
  const c = CHIP[variant] ?? CHIP.muted;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 'var(--radius-sm)', background: c.bg, border: `1px solid ${c.border}`, color: c.text, whiteSpace: 'nowrap' }}>
      {showIcon && <span style={{ fontSize: 9 }}>{c.icon}</span>}
      {dot && <Dot color={c.text} size={5} />}
      {label}
    </span>
  );
}

// ── Module status chip ──────────────────────────────────────────────
export function moduleStatusChip(status: ModuleStatus) {
  switch (status) {
    case 'loading': return <StatusChip label="Loading" variant="info" dot />;
    case 'success': return <StatusChip label="Fresh" variant="success" showIcon />;
    case 'error': return <StatusChip label="Error" variant="error" showIcon />;
    case 'stale': return <StatusChip label="Stale" variant="stale" showIcon />;
    default: return null;
  }
}

// ── EmptyState with variant ─────────────────────────────────────────
export function EmptyState({ icon = '∅', title, sub, variant = 'empty' }: {
  icon?: string; title: string; sub?: string;
  variant?: 'empty' | 'filtered' | 'unavailable';
}) {
  const bgColor = variant === 'unavailable' ? 'var(--red-bg)' : 'var(--bg-surface)';
  const borderColor = variant === 'unavailable' ? 'var(--red-border)' : 'var(--border-subtle)';
  return (
    <div style={{ padding: 48, textAlign: 'center', background: bgColor, borderRadius: 'var(--radius-lg)', border: `1px solid ${borderColor}` }}>
      <div style={{ fontSize: 28, marginBottom: 8, color: 'var(--text-ghost)' }}>{icon}</div>
      <div style={{ fontSize: 13, color: 'var(--text-ghost)' }}>{title}</div>
      {sub && <div style={{ fontSize: 11, marginTop: 8, color: 'var(--text-dim)' }}>{sub}</div>}
    </div>
  );
}

// ── Confidence Legend ────────────────────────────────────────────────
export function ConfidenceLegend() {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-ghost)', flexWrap: 'wrap' }}>
      <span>Confidence:</span>
      <StatusChip label="high" variant="success" showIcon />
      <StatusChip label="moderate" variant="info" showIcon />
      <StatusChip label="low" variant="warning" showIcon />
    </div>
  );
}

// ── Trust Legend — explains proxy, shadow, insufficient ──────────────
export function TrustLegend() {
  return (
    <div style={{ display: 'inline-flex', gap: 6, padding: '6px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', fontSize: 10, color: 'var(--text-ghost)', flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontWeight: 600, marginRight: 4 }}>Trust:</span>
      <StatusChip label="trusted" variant="success" showIcon />
      <StatusChip label="proxy" variant="proxy" showIcon />
      <StatusChip label="shadow" variant="proxy" showIcon />
      <StatusChip label="insufficient" variant="warning" showIcon />
      <StatusChip label="stale" variant="stale" showIcon />
      <StatusChip label="unknown" variant="unknown" showIcon />
    </div>
  );
}
